import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ethers } from 'ethers'
import { usePmWalletStore } from '@/store/pmWalletStore'

export const USDC_POLYGON = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174' as const

const PM_RPC = 'https://polygon-bor-rpc.publicnode.com'
const ERC20_BALANCE_ABI = ['function balanceOf(address) view returns (uint256)']

async function fetchUsdcViaRpc(address: string): Promise<number | null> {
  const provider = new ethers.providers.JsonRpcProvider(PM_RPC, 137)
  const contract = new ethers.Contract(USDC_POLYGON, ERC20_BALANCE_ABI, provider)
  const raw: ethers.BigNumber = await contract.balanceOf(address)
  return parseFloat(ethers.utils.formatUnits(raw, 6))
}

/** Balance for the PM proxy wallet (the only wallet kind in Visual). */
export function useUsdcBalance() {
  const activeWallet = usePmWalletStore((s) => s.activeWallet)
  const pmProxyAddress = usePmWalletStore((s) => s.pmProxyAddress)
  const queryClient = useQueryClient()

  const pmAddress = activeWallet === 'pm' ? pmProxyAddress : undefined

  const { data: pmBalance } = useQuery({
    queryKey: ['pmUsdcBalance', pmAddress],
    queryFn: () => fetchUsdcViaRpc(pmAddress!),
    enabled: Boolean(pmAddress),
    staleTime: 5_000,
    refetchInterval: 5_000,
  })

  const balance = activeWallet === 'pm' ? (pmBalance ?? null) : null
  const refetch = () => queryClient.invalidateQueries({ queryKey: ['pmUsdcBalance', pmAddress] })

  return { balance, refetch }
}

/** USDC balance for a specific address. */
export function useUsdcBalanceForAddress(address: `0x${string}` | string | null | undefined) {
  const normalized = address && typeof address === 'string' && address.startsWith('0x') ? address : undefined

  const { data: rpcBalance } = useQuery({
    queryKey: ['rpcUsdcBalance', normalized],
    queryFn: () => fetchUsdcViaRpc(normalized!),
    enabled: Boolean(normalized),
    staleTime: 5_000,
    refetchInterval: 10_000,
  })

  return rpcBalance ?? null
}
